import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Placeholder } from '@tiptap/extensions'
import { interpolateFilters } from 'helpers/filter-interpolation'
import { buildFieldOptions, buildCalculationOptions } from 'helpers/data-field-options'
import { hasDataFieldTokens } from 'helpers/data-field-tokens'
import { FilterTokenHighlight } from './filter-token-highlight'
import { FilterFieldSuggestion } from './filter-field-suggestion'
import { smSelectStyles, portaledMenuProps } from './searchable-column-select'
import TextSummaryPanel from './text-summary-panel'
import './text-chart-editor.css'

const TextChart = ({ content, params, tableColumns, municipalitySettings, values }) => {
  const renderedContent = useMemo(
    () => interpolateFilters(content, params, tableColumns, municipalitySettings, values),
    [content, params, tableColumns, municipalitySettings, values]
  )

  return (
    <div
      className="p-3 h-100"
      style={{
        wordBreak: 'break-word',
        fontSize: '1rem',
        lineHeight: '1.6'
      }}
      dangerouslySetInnerHTML={{
        __html: renderedContent || '<span style="color: #6c757d">No text content</span>'
      }}
    />
  )
}

TextChart.propTypes = {
  content: PropTypes.string,
  params: PropTypes.object,
  tableColumns: PropTypes.array,
  municipalitySettings: PropTypes.object,
  values: PropTypes.object
}

const ToolbarButton = ({ onClick, isActive, title, children, style }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault()
      onClick()
    }}
    className={isActive ? 'is-active' : ''}
    title={title}
    style={style}
  >
    {children}
  </button>
)

ToolbarButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  style: PropTypes.object
}

const TextChartEditor = ({
  formState,
  setters,
  municipalitySettings,
  columns = [],
  getColumnDisplayName,
  municipality,
  params = {},
  summary = null
}) => {
  const { textContent, textRepeatOnEveryPage } = formState
  const { setTextContent, setTextRepeatOnEveryPage } = setters

  const isFirstSync = useRef(true)
  // A never-focused editor reports its caret at the very start of the document,
  // so a data field picked before the author has clicked into the text would
  // land in front of existing content. Track the first focus and append instead.
  const hasBeenFocused = useRef(false)

  // The editor is built once, but the columns and the dashboard's filters both
  // change under it, so the slash menu and the placeholder read them through a
  // ref rather than closing over the first render's copy.
  const stepsRef = useRef({ fields: [], calculationsFor: () => [] })

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      FilterTokenHighlight,
      Placeholder.configure({
        // A function, so the hint only advertises "/" where there is something
        // for it to insert.
        placeholder: () =>
          stepsRef.current.fields.length > 0
            ? 'Write the chart text — type “/” to insert a data field'
            : 'Write the chart text'
      }),
      FilterFieldSuggestion.configure({
        getFields: () => stepsRef.current.fields,
        getCalculations: (column) => stepsRef.current.calculationsFor(column)
      })
    ],
    content: textContent || '',
    onFocus: () => {
      hasBeenFocused.current = true
    },
    onUpdate: ({ editor }) => {
      setTextContent(editor.getHTML())
    }
  })

  // Sync initial content when modal opens with existing data
  useEffect(() => {
    if (!editor || !isFirstSync.current) return
    isFirstSync.current = false
    editor.commands.setContent(textContent || '', false)
  }, [editor, textContent])

  const brandColors = [1, 2, 3, 4, 5]
    .map((i) => ({ key: `color_${i}`, label: `Color ${i}`, value: municipalitySettings?.[`color_${i}`] }))
    .filter((c) => c.value)

  const colorOptions = [
    { key: 'black', label: 'Default', value: '#212529' },
    ...brandColors,
    { key: 'gray', label: 'Gray', value: '#808080' }
  ]

  const activeColor = editor?.getAttributes('textStyle')?.color

  // Data fields the author can drop into the text, built from the section's
  // columns so every list shows the same names as the rest of the dashboard.
  //
  // Uses the raw column list rather than getFilteredColumns(): that exclusion
  // list is about what makes sense as a chart axis, while anything the report
  // holds is worth quoting back in the text.
  const calculationContext = useMemo(
    () => ({ params, tableColumns: columns, municipalitySettings }),
    [params, columns, municipalitySettings]
  )

  const fieldOptions = useMemo(
    () => buildFieldOptions(columns, getColumnDisplayName, municipality),
    [columns, getColumnDisplayName, municipality]
  )

  stepsRef.current = {
    fields: fieldOptions,
    calculationsFor: (column) => buildCalculationOptions(column, calculationContext)
  }

  // The toolbar picker takes the same two steps as the "/" menu: a field, then
  // what to print about it.
  const [pickedField, setPickedField] = useState(null)
  const calculationSelectRef = useRef(null)

  const calculationOptions = useMemo(
    () => (pickedField ? buildCalculationOptions(pickedField.column, calculationContext) : []),
    [pickedField, calculationContext]
  )

  const hasFields = fieldOptions.length > 0

  // After the render that enables it: a disabled select ignores focus().
  useEffect(() => {
    if (pickedField) calculationSelectRef.current?.focus()
  }, [pickedField])

  const insertCalculation = (option) => {
    if (!option || !editor) return
    // focus() first so the token lands where the caret was before the dropdown
    // took focus, rather than at the top of the document.
    editor
      .chain()
      .focus(hasBeenFocused.current ? null : 'end')
      .insertContent(option.token)
      .run()
    setPickedField(null)
  }

  // A field that is already a token (Count) needs no second step.
  const pickField = (option) => (option?.token ? insertCalculation(option) : setPickedField(option))

  return (
    <Form.Group className="mb-4">
      <Form.Label>Text Content</Form.Label>

      <div className="tiptap-toolbar">
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBold().run()}
          isActive={editor?.isActive('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          isActive={editor?.isActive('italic')}
          title="Italic"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          isActive={editor?.isActive('strike')}
          title="Strikethrough"
        >
          <s>S</s>
        </ToolbarButton>

        <div className="toolbar-divider" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          isActive={editor?.isActive('bulletList')}
          title="Bullet list"
        >
          &#8226;&#8212;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          isActive={editor?.isActive('orderedList')}
          title="Ordered list"
        >
          1&#8212;
        </ToolbarButton>

        <div className="toolbar-divider" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor?.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor?.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          H2
        </ToolbarButton>

        <div className="toolbar-divider" />

        {colorOptions.map((c) => (
          <ToolbarButton
            key={c.key}
            onClick={() => {
              if (activeColor === c.value) {
                editor?.chain().focus().unsetColor().run()
              } else {
                editor?.chain().focus().setColor(c.value).run()
              }
            }}
            isActive={activeColor === c.value}
            title={c.label}
            style={{ width: 20, height: 20, borderRadius: '50%', background: c.value, margin: '4px 2px' }}
          >
            {''}
          </ToolbarButton>
        ))}

        <div className="toolbar-divider" />

        <ToolbarButton
          onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}
          isActive={false}
          title="Clear formatting"
        >
          &#10005;
        </ToolbarButton>

        <div className="toolbar-field-picker">
          <div className="toolbar-field-picker-step">
            <label htmlFor="text-chart-data-field" className="visually-hidden">
              Insert data field
            </label>
            <Select
              inputId="text-chart-data-field"
              options={fieldOptions}
              value={pickedField}
              onChange={pickField}
              isDisabled={!hasFields}
              placeholder={hasFields ? 'Insert data field...' : 'No data fields available'}
              noOptionsMessage={() => 'No matching fields'}
              styles={smSelectStyles}
              {...portaledMenuProps}
            />
          </div>
          <div className="toolbar-field-picker-step">
            <label htmlFor="text-chart-data-calculation" className="visually-hidden">
              Calculation
            </label>
            <Select
              ref={calculationSelectRef}
              inputId="text-chart-data-calculation"
              options={calculationOptions}
              value={null}
              onChange={insertCalculation}
              isDisabled={!pickedField}
              openMenuOnFocus
              placeholder="Calculation..."
              formatOptionLabel={(option, { context }) =>
                context === 'menu' && option.current ? (
                  <>
                    {option.label} <span className="text-muted">({option.current})</span>
                  </>
                ) : (
                  option.label
                )
              }
              styles={smSelectStyles}
              {...portaledMenuProps}
            />
          </div>
        </div>
      </div>

      <div
        className="tiptap-editor-content"
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>

      {summary && (
        <TextSummaryPanel
          {...summary}
          onGenerated={(html) => {
            editor?.commands.setContent(html, false)
            setTextContent(html)
          }}
        />
      )}

      <Form.Check
        type="checkbox"
        className="mt-3"
        label="Repeat on every page in PDF export"
        checked={textRepeatOnEveryPage || false}
        onChange={(e) => setTextRepeatOnEveryPage(e.target.checked)}
      />
    </Form.Group>
  )
}

TextChartEditor.propTypes = {
  formState: PropTypes.object.isRequired,
  setters: PropTypes.object.isRequired,
  municipalitySettings: PropTypes.object,
  columns: PropTypes.array,
  getColumnDisplayName: PropTypes.func.isRequired,
  municipality: PropTypes.object,
  params: PropTypes.object,
  summary: PropTypes.object
}

export const textChartConfig = {
  validate: () => ({}),

  // Text charts fetch only when their text quotes the data: a plain narrative
  // block still costs no request.
  isQueryEnabled: (formData, hasChartId) => !!(hasChartId && hasDataFieldTokens(formData.textContent)),

  buildChartParams: (formData) => ({
    text_content: formData.textContent ?? '',
    repeat_on_every_page: formData.textRepeatOnEveryPage || false
  }),

  buildQueryParams: (baseParams) => baseParams,

  renderOptions: (props) => <TextChartEditor {...props} />
}

export default TextChart
